<template>
  <v-expansion-panel class="audit-change-set" elevation="0">
    <v-expansion-panel-title>
      <div class="d-flex flex-wrap align-center ga-2 py-1 w-100">
        <v-chip :color="eventColor(entry.event)" size="small" variant="tonal">
          <v-icon start size="16">{{ eventIcon(entry.event) }}</v-icon>
          {{ humanize(entry.event) }}
        </v-chip>
        <span v-if="showEntity" class="font-weight-medium">{{ entry.entity_label || entry.entity }}</span>
        <span v-if="showRecord" class="text-body-2 text-medium-emphasis">{{ entry.record_label }}</span>
        <v-chip size="x-small" color="blue-grey" variant="outlined">
          {{ entry.changes?.length || 0 }} field{{ entry.changes?.length === 1 ? "" : "s" }}
        </v-chip>
        <v-spacer />
        <span class="text-caption text-medium-emphasis audit-change-time">{{ formatDateTime(entry.created_at) }}</span>
      </div>
    </v-expansion-panel-title>

    <v-expansion-panel-text>
      <v-alert v-if="!entry.changes?.length" type="info" variant="tonal" density="compact">
        No displayable field changes were recorded for this event.
      </v-alert>
      <div v-else class="audit-change-table-wrap">
        <v-table density="compact" class="audit-change-table">
          <thead>
            <tr>
              <th class="audit-field-column">Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(change, index) in entry.changes" :key="`${change.field}-${index}`">
              <td>
                <div class="font-weight-medium">{{ change.field_label || humanize(change.field) }}</div>
                <div class="text-caption text-medium-emphasis">{{ change.field }}</div>
              </td>
              <td><pre class="audit-value audit-value-before">{{ formatValue(change.old) }}</pre></td>
              <td><pre class="audit-value audit-value-after">{{ formatValue(change.new) }}</pre></td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script setup>
defineProps({
  entry: { type: Object, required: true },
  showEntity: { type: Boolean, default: false },
  showRecord: { type: Boolean, default: false },
});

const eventColors = {
  created: "success",
  updated: "primary",
  deleted: "error",
  restored: "warning",
};

const eventIcons = {
  created: "mdi-plus-circle-outline",
  updated: "mdi-pencil-circle-outline",
  deleted: "mdi-delete-circle-outline",
  restored: "mdi-backup-restore",
};

const eventColor = (event) => eventColors[event] || "blue-grey";
const eventIcon = (event) => eventIcons[event] || "mdi-history";
const humanize = (value) => String(value || "Unknown").replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  if (typeof value === "string" && /^[\[{]/.test(value.trim())) {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (_) {
      return value;
    }
  }
  return String(value);
};
</script>

<style scoped>
.audit-change-set {
  border: 1px solid #e3e9ee;
  border-radius: 10px !important;
  overflow: hidden;
}

.audit-change-set + .audit-change-set {
  margin-top: 8px;
}

.audit-change-table-wrap {
  overflow-x: auto;
}

.audit-change-table {
  min-width: 720px;
  border: 1px solid #edf0f2;
  border-radius: 8px;
}

.audit-change-table th {
  background: #f5f7f9;
  color: #43545e;
  font-weight: 700;
}

.audit-change-table td {
  width: 37.5%;
  max-width: 420px;
  vertical-align: top;
}

.audit-change-table .audit-field-column {
  width: 25%;
}

.audit-value {
  min-height: 22px;
  margin: 0;
  padding: 7px 9px;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.76rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.audit-value-before {
  background: #fff4f4;
  color: #8d3434;
}

.audit-value-after {
  background: #f1faf4;
  color: #24663a;
}

@media (max-width: 700px) {
  .audit-change-time {
    width: 100%;
  }
}
</style>
